import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";

interface ChatBubbleProps {
  role: 'assistant' | 'user';
  content: string;
  isEditable?: boolean;
  onEdit?: () => void;
  className?: string;
}

// Function to render content with styled step headers for assistant messages
const renderAssistantContent = (text: string) => {
  const lines = text.split('\n');
  
  return lines.map((line, index) => {
    // Check for step header pattern: **Step X: ...** or Step X:
    const stepMatch = line.match(/^\*?\*?Step\s+\d+[:\s]/i);
    if (stepMatch) {
      // Remove markdown asterisks and style as header
      const cleanLine = line.replace(/\*\*/g, '');
      return (
        <p key={index} className="text-base md:text-lg font-semibold text-primary mb-2 mt-1">
          {cleanLine}
        </p>
      );
    }
    
    // Handle empty lines
    if (!line.trim()) {
      return <br key={index} />;
    }
    
    return <p key={index} className="mb-1">{line}</p>;
  });
};

const ChatBubble = ({ role, content, isEditable, onEdit, className }: ChatBubbleProps) => {
  const isAssistant = role === 'assistant';
  const isUser = role === 'user';

  return (
    <div
      className={cn(
        "flex w-full fade-in-up group",
        isAssistant ? "justify-start" : "justify-end",
        className
      )}
    >
      <div
        className={cn(
          "relative max-w-[85%] rounded-2xl px-4 py-3 text-base leading-relaxed",
          isAssistant
            ? "bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 border border-border/50 text-foreground rounded-tl-sm"
            : "bg-primary/80 text-foreground rounded-tr-sm"
        )}
      >
        {isAssistant ? (
          <div className="whitespace-pre-wrap">{renderAssistantContent(content)}</div>
        ) : (
          <p className="whitespace-pre-wrap">{content}</p>
        )}
        
        {/* Edit button for user messages */}
        {isUser && isEditable && onEdit && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onEdit}
            className="absolute -left-10 top-1/2 -translate-y-1/2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
            title="Edit this response"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
